import axios, { AxiosInstance } from 'axios';
import {
    PlannedTaskAction,
    ProjectId,
    TaskAction,
    TaskId,
} from '../core/model';
import { Environment } from '../Environment';
import { FeatureFlagService } from '../feature-flag/feature-flag-service';
import {
    ReadAutomationsResponse,
    ReadAutomationStatusResponse,
    shapes as automationShapes,
} from './automation-model';
import {
    AddProjectCommand,
    AddProjectResponse,
    LeaveProjectCommand,
    ReadArchivedProjectsResponse,
    ReadProjectTasksResponse,
    RenameProjectCommand,
    SetProjectColorCommand,
    shapes as projectShapes,
    UpdateProjectCommand,
} from './project-model';
import {
    AddTaskCommand,
    RenameTaskCommand,
    shapes as taskShapes,
    TaskDto,
} from './task-model';
import {
    GetUserSubscription,
    ReadSessionResponse,
    shapes as userShapes,
} from './user-model';
import {
    ReadWorkspaceResponse,
    shapes as workspaceShapes,
} from './workspace-model';
import {
    shapes as workspaceVersionShapes,
    WorkspaceVersionDto,
} from './workspace-version-model';

export default class Api {
    private http: AxiosInstance;

    constructor() {
        this.http = axios.create({
            baseURL: 'http://localhost:8080/api',
            headers: {
                'Content-type': 'application/json',
            },
        });

        // Add request interceptor to handle dynamic auth headers
        this.http.interceptors.request.use((config) => {
            const token = localStorage.getItem('hustleToken');
            if (token && token !== 'null' && token !== 'undefined') {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        });
    }

    public static defaultApi() {
        return new Api();
    }

    public readSession = (): Promise<ReadSessionResponse> => {
        return this.http
            .get(`${Environment.backendUrl}/api/v1/session`)
            .then((result) => {
                const userSession = userShapes.ReadSessionResponse.parse(
                    result.data
                );
                FeatureFlagService.updateUserFlags(userSession.userId);
                return userSession;
            });
    };

    public getUserSubscription = (): Promise<GetUserSubscription> => {
        return this.http
            .get(`${Environment.backendUrl}/api/v1/user/plan`)
            .then((result) =>
                userShapes.GetUserSubscription.parse(result.data)
            );
    };

    public fetchWorkspace = (
        workspaceId: string
    ): Promise<ReadWorkspaceResponse> => {
        return this.http
            .get(`${Environment.backendUrl}/workspaces/${workspaceId}`)
            .then((result) =>
                workspaceShapes.ReadWorkspaceResponse.parse(result.data)
            );
    };

    public fetchArchivedProjects = (
        workspaceId: string
    ): Promise<ReadArchivedProjectsResponse> => {
        return this.http
            .get(
                `${Environment.backendUrl}/api/v1/workspaces/${workspaceId}/archived-projects`
            )
            .then((result) =>
                projectShapes.ReadArchivedProjectsResponse.parse(result.data)
            );
    };

    public fetchProjectTasks = (
        projectId: string
    ): Promise<ReadProjectTasksResponse> => {
        return this.http
            .get(`${Environment.backendUrl}/api/v1/projects/${projectId}/tasks`)
            .then((result) => {
                return projectShapes.ReadProjectTasksResponse.parse(
                    result.data
                );
            });
    };

    public addProject = (
        command: AddProjectCommand
    ): Promise<AddProjectResponse> => {
        const parsed = projectShapes.AddProjectCommand.parse(command);
        return this.http
            .post(`${Environment.backendUrl}/api/v1/projects`, parsed)
            .then((result) =>
                projectShapes.AddProjectResponse.parse(result.data)
            );
    };

    public renameProject = (
        projectId: ProjectId,
        command: RenameProjectCommand
    ): Promise<any> => {
        const parsed = projectShapes.RenameProjectCommand.parse(command);
        return this.http
            .put(
                `${Environment.backendUrl}/api/v1/projects/${projectId}/rename`,
                parsed
            )
            .then((result) => {
                return result.data;
            });
    };

    public moveProject = (
        workspaceId: string,
        projectId: string,
        itemIndex: number,
        newIndex: number
    ) => {
        return this.http
            .put(
                `${Environment.backendUrl}/api/v1/workspaces/${workspaceId}/move-project`,
                {
                    projectId: projectId,
                    index: newIndex,
                }
            )
            .then(() => {
                return {
                    itemIndex: itemIndex,
                    index: newIndex,
                };
            });
    };

    public setProjectColor = (
        projectId: ProjectId,
        command: SetProjectColorCommand
    ): Promise<any> => {
        const parsed = projectShapes.SetProjectColorCommand.parse(command);
        return this.http
            .put(
                `${Environment.backendUrl}/api/v1/projects/${projectId}/color`,
                parsed
            )
            .then((result) => {
                return result.data;
            });
    };

    public archiveProject = (projectId: string) => {
        return this.http.post(
            `${Environment.backendUrl}/api/v1/projects/${projectId}/archive`
        );
    };

    public unarchiveProject = (projectId: string) => {
        return this.http
            .post(
                `${Environment.backendUrl}/api/v1/projects/${projectId}/unarchive`
            )
            .then((result) => result.data);
    };

    public deleteProject = (projectId: string): Promise<any> => {
        return this.http
            .delete(`${Environment.backendUrl}/api/v1/projects/${projectId}`)
            .then(() => projectId);
    };

    public leaveProject = (
        projectId: string,
        command: LeaveProjectCommand
    ): Promise<any> => {
        const parsed = projectShapes.LeaveProjectCommand.parse(command);
        return this.http
            .post(
                `${Environment.backendUrl}/api/v1/projects/${projectId}/leave`,
                parsed
            )
            .then(() => projectId);
    };

    public addTask = (command: AddTaskCommand): Promise<TaskDto> => {
        const parsed = taskShapes.AddTaskCommand.parse(command);
        return this.http
            .post(`${Environment.backendUrl}/api/v1/tasks`, parsed)
            .then((result) => taskShapes.TaskDto.parse(result.data));
    };

    public deleteTask = (
        taskId: TaskId,
        command: UpdateProjectCommand
    ): Promise<any> => {
        const parsed = projectShapes.UpdateProjectCommand.parse(command);
        return this.http.post(
            `${Environment.backendUrl}/api/v1/tasks/${taskId}/delete`,
            parsed
        );
    };

    public renameTask = (
        taskId: TaskId,
        command: RenameTaskCommand
    ): Promise<any> => {
        const parsed = taskShapes.RenameTaskCommand.parse(command);
        return this.http.put(
            `${Environment.backendUrl}/api/v1/tasks/${taskId}/rename`,
            parsed
        );
    };

    public taskAction = (
        taskId: TaskId,
        action: TaskAction,
        command: UpdateProjectCommand
    ): Promise<any> => {
        const parsed = projectShapes.UpdateProjectCommand.parse(command);
        return this.http.put(
            `${
                Environment.backendUrl
            }/api/v1/workspace/tasks/${taskId}/${action.toLowerCase()}`,
            parsed
        );
    };

    public plannedTaskAction = (
        taskId: TaskId,
        action: PlannedTaskAction,
        command: UpdateProjectCommand
    ): Promise<any> => {
        const parsed = projectShapes.UpdateProjectCommand.parse(command);
        return this.http.put(
            `${
                Environment.backendUrl
            }/api/v1/tasks/${taskId}/${action.toLowerCase()}`,
            parsed
        );
    };

    public hideCompletedTasks = (): Promise<any> => {
        return this.http.post(
            `${Environment.backendUrl}/api/v1/workspace/hide-completed`,
            {}
        );
    };

    public clearAllPlannedTasks = (): Promise<any> => {
        return this.http.post(
            `${Environment.backendUrl}/api/v1/workspace/idle-all-planned`,
            {}
        );
    };

    public fetchAutomationStatus =
        (): Promise<ReadAutomationStatusResponse> => {
            return this.http
                .get(`${Environment.backendUrl}/api/v1/automation-status`)
                .then((result) =>
                    automationShapes.ReadAutomationStatusResponse.parse(
                        result.data
                    )
                );
        };

    createAutomation(
        workspaceId: string | null | undefined,
        automationName: string | undefined,
        dayOfMonth: number,
        steps: {
            type: 'TASK';
            payload: any;
        }[]
    ) {
        return this.http.post(`${Environment.backendUrl}/api/v1/automations`, {
            workspaceId: workspaceId,
            name: automationName || 'Monthly Automation ' + dayOfMonth,
            trigger: {
                frequency: 'MONTHLY',
                dayOfMonth,
            },
            steps,
        });
    }

    public fetchAutomations = (): Promise<ReadAutomationsResponse> => {
        return this.http
            .get(`${Environment.backendUrl}/api/v1/automations`)
            .then((result) =>
                automationShapes.ReadAutomationsResponse.parse(result.data)
            );
    };

    public deleteAutomation = (
        id: string
    ): Promise<ReadAutomationsResponse> => {
        return this.http.delete(
            `${Environment.backendUrl}/api/v1/automations/${id}`
        );
    };

    public fetchWorkspaceVersion = (): Promise<WorkspaceVersionDto> => {
        return this.http
            .get(`${Environment.backendUrl}/api/v1/dashboard/version`)
            .then((result) =>
                workspaceVersionShapes.WorkspaceVersionDto.parse(result.data)
            );
    };
}
